"use client";

import { useCallback, useMemo } from "react";

import type { FacetValue } from "../types/filters.type";

type SortableOption = Pick<FacetValue, "label" | "value" | "is_selected">;

type UseFilterOptionListParams<T extends SortableOption> = {
  options: T[];
  selectedValues: string[];
  selector: "Multiple" | "Single";
  onSelectionChange: (values: string[]) => void;
};

export function useFilterOptionList<T extends SortableOption>({
  options,
  selectedValues,
  selector,
  onSelectionChange,
}: UseFilterOptionListParams<T>) {
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  // Sort order is derived from the server snapshot (is_selected), not from
  // live pending selections. This means the list only reorders when a new
  // options reference arrives (live facet update), never during user interaction.
  const sortedOptions = useMemo(() => {
    const selected: T[] = [];
    const unselected: T[] = [];

    for (const option of options) {
      if (option.is_selected) {
        selected.push(option);
      } else {
        unselected.push(option);
      }
    }

    selected.sort((a, b) => a.label.localeCompare(b.label));
    unselected.sort((a, b) => a.label.localeCompare(b.label));

    return [...selected, ...unselected];
  }, [options]);

  const handleToggle = useCallback(
    (value: string, checked: boolean) => {
      if (selector === "Multiple") {
        const updated = checked
          ? [...selectedValues, value]
          : selectedValues.filter((v) => v !== value);
        onSelectionChange(updated);
      }
    },
    [selector, selectedValues, onSelectionChange],
  );

  const handleRadioChange = useCallback(
    (value: string) => {
      onSelectionChange([value]);
    },
    [onSelectionChange],
  );

  return { sortedOptions, selectedSet, handleToggle, handleRadioChange };
}
